import React, { useEffect, useMemo, useState } from 'react';
import { Building2, Edit, KeyRound, PlusCircle, RefreshCcw, ShieldAlert, Smartphone, Users } from 'lucide-react';
import { toast } from 'sonner';
import { Badge } from '../../../components/ui/badge';
import { Button } from '../../../components/ui/button';
import { Checkbox } from '../../../components/ui/checkbox';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../../components/ui/dialog';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Switch } from '../../../components/ui/switch';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../../components/ui/tabs';
import AreasDialog from '../../../components/rbac/users/AreasDialog';
import OverridesPanel from '../../../components/rbac/users/OverridesPanel';
import RoleAssignmentEditor from '../../../components/rbac/users/RoleAssignmentEditor';
import { usePermissions } from '../../../components/rbac/use-permission';
import { api, userMfaAdminApi, type MfaDevice } from '../../../lib/api';
import { P } from '../../../lib/rbac/permissions.generated';
import {
  ALTROLLEN_LABELS,
  adminUsersApi,
  benutzerName,
  type Katalog,
  type RbacBenutzer,
  type RollenZuweisung,
} from '../../../lib/rbac/userAdmin';

const errorText = (e: unknown, fallback: string) => (e instanceof Error && e.message ? e.message : fallback);

/** Kurzbeschreibung einer Zuweisung für Tabelle und Badges. */
const zuweisungText = (
  z: RollenZuweisung,
  katalog: Katalog | null,
): string => {
  const role = katalog?.rollen.find((r) => r.key === z.role_key);
  const label = role?.label ?? z.role_key;
  if (z.scope_type !== 'location') return label;
  const names = z.standort_ids.map((id) => katalog?.standorte.find((s) => s.id === id)?.name ?? id);
  return `${label} · ${names.join(', ')}`;
};

const UserManagement = () => {
  const { can } = usePermissions();
  const company = { minScope: 'company' as const };
  const canCreate = can(P.admin.benutzer_und_rollen.benutzer_anlegen, company);
  const canEdit = can(P.admin.benutzer_und_rollen.benutzer_bearbeiten, company);
  const canToggle = can(P.admin.benutzer_und_rollen.benutzer_de_aktivieren, company);
  const canRoles = can(P.admin.benutzer_und_rollen.rollen_vergeben, company);
  const canAccess = can(P.admin.benutzer_und_rollen.standortzuordnung_verwalten, company);
  const canOverrides = can(P.admin.benutzer_und_rollen.berechtigungen_bearbeiten, company);
  const canMfaPolicy = can(P.admin.sicherheit['2fa_richtlinien_verwalten']);
  const canSessions = can(P.admin.sicherheit.benutzersitzungen_verwalten);

  const [katalog, setKatalog] = useState<Katalog | null>(null);
  const [users, setUsers] = useState<RbacBenutzer[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');
  const [showInactive, setShowInactive] = useState(false);
  const [areasOpen, setAreasOpen] = useState(false);

  const [createOpen, setCreateOpen] = useState(false);
  const [cVorname, setCVorname] = useState('');
  const [cNachname, setCNachname] = useState('');
  const [cEmail, setCEmail] = useState('');
  const [cPassword, setCPassword] = useState('');

  const [selected, setSelected] = useState<RbacBenutzer | null>(null);
  const [tab, setTab] = useState('rollen');
  const [eVorname, setEVorname] = useState('');
  const [eNachname, setENachname] = useState('');
  const [eEmail, setEEmail] = useState('');
  const [rollen, setRollen] = useState<RollenZuweisung[]>([]);
  const [teamStandorte, setTeamStandorte] = useState<number[]>([]);
  const [funktionen, setFunktionen] = useState<string[]>([]);
  const [bereiche, setBereiche] = useState<string[]>([]);
  const [zugang, setZugang] = useState<number[]>([]);
  const [saving, setSaving] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [devices, setDevices] = useState<MfaDevice[] | null>(null);

  const load = async () => {
    setLoading(true);
    try {
      const [k, list] = await Promise.all([adminUsersApi.katalog(), adminUsersApi.list()]);
      setKatalog(k);
      setUsers(list);
    } catch (e) {
      toast.error(errorText(e, 'Benutzer konnten nicht geladen werden'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
  }, []);

  const visibleUsers = useMemo(() => {
    const q = filter.trim().toLowerCase();
    return users.filter(
      (u) =>
        (showInactive || u.aktiv) &&
        (!q || benutzerName(u).toLowerCase().includes(q) || u.email.toLowerCase().includes(q)),
    );
  }, [users, filter, showInactive]);

  const replaceUser = (next: RbacBenutzer) => {
    setUsers((prev) => prev.map((u) => (u.account_id === next.account_id ? next : u)));
    setSelected((prev) => (prev?.account_id === next.account_id ? next : prev));
  };

  const openEdit = (user: RbacBenutzer, initialTab = 'rollen') => {
    setSelected(user);
    setTab(initialTab);
    setEVorname(user.vorname);
    setENachname(user.nachname);
    setEEmail(user.email);
    setRollen(user.rollen.map((r) => ({ ...r })));
    setTeamStandorte(user.standort_team_ids);
    setFunktionen(user.funktionen ?? []);
    setBereiche(user.verantwortungsbereich_ids ?? []);
    setZugang(user.standort_ids);
    setNewPassword('');
    setDevices(null);
  };

  const handleCreate = async () => {
    if (!cVorname.trim() || !cNachname.trim() || !cEmail.trim() || cPassword.length < 8) {
      toast.error('Bitte Name, E-Mail und ein Passwort mit mindestens 8 Zeichen angeben');
      return;
    }
    try {
      const created = await adminUsersApi.create({
        vorname: cVorname, nachname: cNachname, email: cEmail, password: cPassword,
      });
      setUsers((prev) => [...prev, created]);
      setCreateOpen(false);
      setCVorname(''); setCNachname(''); setCEmail(''); setCPassword('');
      toast.success('Benutzer angelegt — jetzt Rollen zuweisen');
      openEdit(created, 'rollen');
    } catch (e) {
      toast.error(errorText(e, 'Anlegen fehlgeschlagen'));
    }
  };

  const saveStammdaten = async () => {
    if (!selected) return;
    setSaving(true);
    try {
      replaceUser(await adminUsersApi.update(selected.account_id, { vorname: eVorname, nachname: eNachname, email: eEmail }));
      toast.success('Stammdaten gespeichert');
    } catch (e) {
      toast.error(errorText(e, 'Speichern fehlgeschlagen'));
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (aktiv: boolean) => {
    if (!selected) return;
    if (!aktiv && !confirm(`${benutzerName(selected)} deaktivieren? Die Anmeldung ist danach nicht mehr möglich.`)) return;
    try {
      replaceUser(await adminUsersApi.setActive(selected.account_id, aktiv));
      toast.success(aktiv ? 'Benutzer aktiviert' : 'Benutzer deaktiviert');
    } catch (e) {
      toast.error(errorText(e, 'Status konnte nicht geändert werden'));
    }
  };

  const saveZuordnungen = async () => {
    if (!selected) return;
    const incomplete = rollen.find((r) => r.scope_type === 'location' && r.standort_ids.length === 0);
    if (incomplete) {
      toast.error('Standortbezogene Rollen brauchen mindestens einen Standort');
      return;
    }
    setSaving(true);
    try {
      const next = await adminUsersApi.setZuordnungen(selected.account_id, {
        rollen,
        standort_team_ids: teamStandorte,
        funktionen,
        verantwortungsbereich_ids: bereiche,
        ...(canAccess ? { standort_ids: zugang } : {}),
      });
      replaceUser(next);
      openEdit(next, 'rollen');
      toast.success('Rollen und Zuordnungen gespeichert');
    } catch (e) {
      toast.error(errorText(e, 'Speichern fehlgeschlagen'));
    } finally {
      setSaving(false);
    }
  };

  const reloadSelected = async () => {
    if (!selected) return;
    replaceUser(await adminUsersApi.get(selected.account_id));
  };

  const changePassword = async () => {
    if (!selected?.person_id) return;
    if (newPassword.length < 8) {
      toast.error('Das Passwort braucht mindestens 8 Zeichen');
      return;
    }
    try {
      await api.post(`/api/users/${selected.person_id}/password`, { new_password: newPassword });
      setNewPassword('');
      toast.success('Passwort geändert');
    } catch (e) {
      toast.error(errorText(e, 'Passwortänderung fehlgeschlagen'));
    }
  };

  const mfaPolicy = async (payload: { mfa_exception?: boolean; reset_mfa?: boolean; mfa_enabled?: boolean }) => {
    if (!selected?.person_id) return;
    try {
      await userMfaAdminApi.updatePolicy(selected.person_id, payload);
      await reloadSelected();
      toast.success('2FA-Einstellung gespeichert');
    } catch (e) {
      toast.error(errorText(e, '2FA-Einstellung konnte nicht gespeichert werden'));
    }
  };

  const loadDevices = async () => {
    if (!selected?.person_id) return;
    try {
      setDevices(await userMfaAdminApi.listDevices(selected.person_id));
    } catch (e) {
      toast.error(errorText(e, 'Geräte konnten nicht geladen werden'));
    }
  };

  const revokeDevice = async (device: MfaDevice) => {
    if (!selected?.person_id) return;
    const ref = device.device_id ? `device:${device.device_id}` : `token:${device.id}`;
    try {
      await userMfaAdminApi.revokeDevice(selected.person_id, ref);
      setDevices((prev) => (prev ?? []).filter((d) => d.id !== device.id));
      toast.success('Gerät abgemeldet');
    } catch (e) {
      toast.error(errorText(e, 'Gerät konnte nicht abgemeldet werden'));
    }
  };

  const toggle = <T,>(list: T[], item: T, on: boolean): T[] =>
    on ? [...list.filter((x) => x !== item), item] : list.filter((x) => x !== item);

  const standorte = katalog?.standorte ?? [];

  return (
    <div className="mx-auto max-w-7xl space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold leading-7">Benutzerverwaltung</h1>
          <p className="text-muted-foreground">Mitarbeiterkonten, Rollen, Zuständigkeiten und individuelle Rechte</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="outline" onClick={load} disabled={loading}>
            <RefreshCcw className="mr-2 h-4 w-4" /> Aktualisieren
          </Button>
          {canRoles && (
            <Button variant="outline" onClick={() => setAreasOpen(true)} disabled={!katalog}>
              <Building2 className="mr-2 h-4 w-4" /> Verantwortungsbereiche
            </Button>
          )}
          {canCreate && (
            <Button onClick={() => setCreateOpen(true)}>
              <PlusCircle className="mr-2 h-4 w-4" /> Neuer Benutzer
            </Button>
          )}
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-4">
        <Input uiSize="md" className="max-w-sm" placeholder="Name oder E-Mail suchen"
          value={filter} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFilter(e.target.value)} />
        <label className="flex items-center gap-2 text-sm">
          <Switch checked={showInactive} onCheckedChange={setShowInactive} /> Deaktivierte anzeigen
        </label>
      </div>

      {loading ? (
        <div className="rounded-lg border bg-card p-8 text-center">
          <Users className="mx-auto h-12 w-12 text-muted-foreground" />
          <h2 className="mt-4 text-lg font-semibold">Lade Benutzer…</h2>
        </div>
      ) : (
        <div className="rounded-lg border bg-card p-2 md:p-3">
          <Table density="compact">
            <TableHeader>
              <TableRow>
                <TableHead density="compact">Name</TableHead>
                <TableHead density="compact">Rollen</TableHead>
                <TableHead density="compact">2FA</TableHead>
                <TableHead density="compact">Standortzugang</TableHead>
                <TableHead density="compact" className="text-right">Aktionen</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {visibleUsers.length === 0 && (
                <TableRow>
                  <TableCell density="compact" colSpan={5} className="text-muted-foreground">
                    Keine Benutzer. Lehrer und Kunden werden in der Personenverwaltung geführt.
                  </TableCell>
                </TableRow>
              )}
              {visibleUsers.map((u) => (
                <TableRow key={u.account_id} className={u.aktiv ? '' : 'opacity-60'}>
                  <TableCell density="compact">
                    <div className="font-medium">{benutzerName(u)}</div>
                    <div className="text-xs text-muted-foreground">{u.email}{u.aktiv ? '' : ' · deaktiviert'}</div>
                  </TableCell>
                  <TableCell density="compact">
                    <div className="flex flex-wrap gap-1">
                      {u.rollen.map((r) => (
                        <Badge key={r.id} size="sm" variant="info">{zuweisungText(r, katalog)}</Badge>
                      ))}
                      {u.altrollen.map((a) => (
                        <Badge key={`${a.rolle}-${a.standort_id}`} size="sm" variant="outline" className="border-warning/60 text-warning"
                          title="Bestandsrolle ohne Wirkung — Rollen neu zuweisen">
                          {ALTROLLEN_LABELS[a.rolle] ?? a.rolle}
                        </Badge>
                      ))}
                      {u.rollen.length === 0 && u.altrollen.length === 0 && (
                        <span className="text-xs text-muted-foreground">keine</span>
                      )}
                    </div>
                  </TableCell>
                  <TableCell density="compact">
                    <div className="flex flex-wrap gap-1">
                      <Badge size="sm" variant={u.mfa_enabled ? 'success' : 'secondary'}>{u.mfa_enabled ? 'Aktiv' : 'Inaktiv'}</Badge>
                      {u.sensible_rechte && <Badge size="sm" variant="outline" title="Hat Rechte, für die die 2FA-Richtlinie gilt">sensibel</Badge>}
                      {u.mfa_exception && <Badge size="sm" variant="destructive">Ausnahme</Badge>}
                    </div>
                  </TableCell>
                  <TableCell density="compact" className="text-sm">
                    {u.standort_ids.map((id) => standorte.find((s) => s.id === id)?.name ?? id).join(', ') || '—'}
                  </TableCell>
                  <TableCell density="compact" className="text-right">
                    <Button variant="outline" size="sm" onClick={() => openEdit(u)}>
                      <Edit className="mr-2 h-4 w-4" /> Bearbeiten
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Neuen Benutzer anlegen</DialogTitle>
            <DialogDescription>Rollen und Zuständigkeiten vergeben Sie im nächsten Schritt.</DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-1">
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Vorname</Label><Input uiSize="md" value={cVorname} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setCVorname(e.target.value)} /></div>
              <div><Label>Nachname</Label><Input uiSize="md" value={cNachname} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setCNachname(e.target.value)} /></div>
            </div>
            <div><Label>E-Mail</Label><Input uiSize="md" type="email" value={cEmail} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setCEmail(e.target.value)} /></div>
            <div><Label>Passwort</Label><Input uiSize="md" type="password" value={cPassword} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setCPassword(e.target.value)} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)}>Abbrechen</Button>
            <Button onClick={handleCreate}>Anlegen</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {katalog && (
        <AreasDialog
          open={areasOpen}
          onOpenChange={setAreasOpen}
          bereiche={katalog.verantwortungsbereiche ?? []}
          standorte={standorte}
          onChanged={(next) => setKatalog({ ...katalog, verantwortungsbereiche: next })}
        />
      )}

      <Dialog open={selected !== null} onOpenChange={(open: boolean) => { if (!open) setSelected(null); }}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-4xl">
          {selected && katalog && (
            <>
              <DialogHeader>
                <DialogTitle>{benutzerName(selected)}</DialogTitle>
                <DialogDescription>{selected.email}</DialogDescription>
              </DialogHeader>
              <Tabs value={tab} onValueChange={setTab}>
                <TabsList>
                  <TabsTrigger value="rollen">Rollen & Zuordnung</TabsTrigger>
                  {canOverrides && <TabsTrigger value="rechte">Individuelle Rechte</TabsTrigger>}
                  <TabsTrigger value="stammdaten">Stammdaten</TabsTrigger>
                  <TabsTrigger value="sicherheit">Sicherheit</TabsTrigger>
                </TabsList>

                <TabsContent value="rollen" className="space-y-5 pt-4">
                  {selected.altrollen.length > 0 && (
                    <div className="flex gap-2 rounded-md border border-warning/40 bg-warning/10 p-3 text-sm">
                      <ShieldAlert className="h-4 w-4 shrink-0" />
                      <span>
                        Bestandsrollen ({selected.altrollen.map((a) => ALTROLLEN_LABELS[a.rolle] ?? a.rolle).join(', ')})
                        wirken nicht mehr. Beim Speichern werden sie entfernt — bitte die passenden Rollen zuweisen.
                      </span>
                    </div>
                  )}
                  <section className="space-y-2">
                    <h3 className="text-sm font-semibold">Rollen</h3>
                    <RoleAssignmentEditor katalog={katalog} value={rollen} onChange={setRollen} disabled={!canRoles} />
                  </section>
                  <section className="space-y-2">
                    <h3 className="text-sm font-semibold">Standort-Teams</h3>
                    <p className="text-xs text-muted-foreground">Bestimmen den Team-Scope (z. B. Office, Finance): Daten der Standorte dieser Teams.</p>
                    <div className="flex flex-wrap gap-x-4 gap-y-2">
                      {standorte.map((s) => (
                        <label key={s.id} className="flex items-center gap-2 text-sm">
                          <Checkbox checked={teamStandorte.includes(s.id)} disabled={!canRoles}
                            onCheckedChange={(c) => setTeamStandorte(toggle(teamStandorte, s.id, c === true))} />
                          {s.name}
                        </label>
                      ))}
                    </div>
                  </section>
                  <section className="space-y-2">
                    <h3 className="text-sm font-semibold">Funktionsteams</h3>
                    <p className="text-xs text-muted-foreground">Zuständigkeit für standortübergreifende Inhalte (z. B. Website für die Rolle Content).</p>
                    <div className="flex flex-wrap gap-x-4 gap-y-2">
                      {katalog.funktionen.map((f) => (
                        <label key={f.key} className="flex items-center gap-2 text-sm">
                          <Checkbox checked={funktionen.includes(f.key)} disabled={!canRoles}
                            onCheckedChange={(c) => setFunktionen(toggle(funktionen, f.key, c === true))} />
                          {f.label}
                        </label>
                      ))}
                    </div>
                  </section>
                  <section className="space-y-2">
                    <h3 className="text-sm font-semibold">Verantwortungsbereiche</h3>
                    <p className="text-xs text-muted-foreground">Nur für Rollen mit Unternehmens-Scope über einen Bereich (CEO).</p>
                    {(katalog.verantwortungsbereiche ?? []).length === 0 ? (
                      <p className="text-sm text-muted-foreground">Noch keine Bereiche angelegt.</p>
                    ) : (
                      <div className="flex flex-wrap gap-x-4 gap-y-2">
                        {(katalog.verantwortungsbereiche ?? []).map((b) => (
                          <label key={b.id} className="flex items-center gap-2 text-sm">
                            <Checkbox checked={bereiche.includes(b.id)} disabled={!canRoles}
                              onCheckedChange={(c) => setBereiche(toggle(bereiche, b.id, c === true))} />
                            {b.name}
                          </label>
                        ))}
                      </div>
                    )}
                  </section>
                  {canAccess && (
                    <section className="space-y-2">
                      <h3 className="text-sm font-semibold">Standortzugang</h3>
                      <p className="text-xs text-muted-foreground">Unter welchen Standorten der Benutzer in der Standort-Benutzerverwaltung erscheint.</p>
                      <div className="flex flex-wrap gap-x-4 gap-y-2">
                        {standorte.map((s) => (
                          <label key={s.id} className="flex items-center gap-2 text-sm">
                            <Checkbox checked={zugang.includes(s.id)}
                              onCheckedChange={(c) => setZugang(toggle(zugang, s.id, c === true))} />
                            {s.name}
                          </label>
                        ))}
                      </div>
                    </section>
                  )}
                  {canRoles && (
                    <div className="flex justify-end">
                      <Button onClick={saveZuordnungen} disabled={saving}>Rollen & Zuordnung speichern</Button>
                    </div>
                  )}
                </TabsContent>

                {canOverrides && (
                  <TabsContent value="rechte" className="pt-4">
                    <OverridesPanel mode="admin" accountId={selected.account_id} katalog={katalog} />
                  </TabsContent>
                )}

                <TabsContent value="stammdaten" className="space-y-4 pt-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div><Label>Vorname</Label><Input uiSize="md" value={eVorname} disabled={!canEdit} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEVorname(e.target.value)} /></div>
                    <div><Label>Nachname</Label><Input uiSize="md" value={eNachname} disabled={!canEdit} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setENachname(e.target.value)} /></div>
                  </div>
                  <div><Label>E-Mail</Label><Input uiSize="md" type="email" value={eEmail} disabled={!canEdit} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEEmail(e.target.value)} /></div>
                  {canEdit && <div className="flex justify-end"><Button onClick={saveStammdaten} disabled={saving}>Stammdaten speichern</Button></div>}
                  {canToggle && (
                    <div className="flex items-center justify-between rounded-md border p-3">
                      <div>
                        <div className="text-sm font-medium">Konto aktiv</div>
                        <div className="text-xs text-muted-foreground">Deaktivierte Konten können sich nicht mehr anmelden.</div>
                      </div>
                      <Switch checked={selected.aktiv} onCheckedChange={toggleActive} />
                    </div>
                  )}
                </TabsContent>

                <TabsContent value="sicherheit" className="space-y-4 pt-4">
                  {canEdit && selected.person_id && (
                    <div className="space-y-2 rounded-md border p-3">
                      <div className="text-sm font-medium">Passwort setzen</div>
                      <div className="flex gap-2">
                        <Input uiSize="md" type="password" value={newPassword} placeholder="Neues Passwort"
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNewPassword(e.target.value)} />
                        <Button onClick={changePassword}><KeyRound className="mr-2 h-4 w-4" /> Setzen</Button>
                      </div>
                    </div>
                  )}
                  <div className="space-y-3 rounded-md border p-3">
                    <div className="flex flex-wrap items-center gap-2 text-sm font-medium">
                      Zwei-Faktor-Anmeldung
                      <Badge size="sm" variant={selected.mfa_enabled ? 'success' : 'secondary'}>{selected.mfa_enabled ? 'eingerichtet' : 'nicht eingerichtet'}</Badge>
                      {selected.sensible_rechte && <Badge size="sm" variant="outline">sensible Rechte</Badge>}
                    </div>
                    <p className="text-xs text-muted-foreground">
                      {selected.sensible_rechte
                        ? 'Der Benutzer hat sensible Rechte. Ist die 2FA-Richtlinie eingeschaltet, muss er 2FA einrichten — außer mit Ausnahme.'
                        : 'Keine sensiblen Rechte — die 2FA-Richtlinie betrifft diesen Benutzer nicht.'}
                    </p>
                    {canMfaPolicy && selected.person_id && (
                      <div className="flex flex-wrap items-center gap-4">
                        <label className="flex items-center gap-2 text-sm">
                          <Switch checked={selected.mfa_exception} onCheckedChange={(v: boolean) => mfaPolicy({ mfa_exception: v })} />
                          Von der 2FA-Pflicht ausnehmen
                        </label>
                        {selected.mfa_enabled && (
                          <Button variant="outline" size="sm"
                            onClick={() => { if (confirm('2FA für diesen Benutzer zurücksetzen?')) void mfaPolicy({ reset_mfa: true, mfa_enabled: false }); }}>
                            2FA zurücksetzen
                          </Button>
                        )}
                      </div>
                    )}
                  </div>
                  {canSessions && selected.person_id && (
                    <div className="space-y-2 rounded-md border p-3">
                      <div className="flex items-center justify-between">
                        <div className="text-sm font-medium">Angemeldete Geräte</div>
                        <Button variant="outline" size="sm" onClick={loadDevices}><Smartphone className="mr-2 h-4 w-4" /> Laden</Button>
                      </div>
                      {devices !== null && devices.length === 0 && <p className="text-sm text-muted-foreground">Keine aktiven Geräte.</p>}
                      {(devices ?? []).map((d) => (
                        <div key={`${d.id}-${d.device_id ?? ''}`} className="flex items-center justify-between gap-3 rounded border p-2 text-sm">
                          <span>{d.device_name || 'Unbekanntes Gerät'} · seit {new Date(d.created_at).toLocaleDateString('de-DE')}</span>
                          <Button variant="destructive" size="sm" onClick={() => revokeDevice(d)}>Abmelden</Button>
                        </div>
                      ))}
                    </div>
                  )}
                </TabsContent>
              </Tabs>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default UserManagement;
